/**
 * The paragraph editor's surface ⇄ runs: a **whitelist** walker one way, a
 * builder that uses no HTML parser the other.
 *
 * `text-runs.ts` holds the data and the edits. This file is the only code that
 * touches the `contentEditable` surface, and it does two things:
 *
 *   - {@link readRuns} walks the surface and returns runs. It recognises four
 *     marks, and only in the exact form {@link renderRuns} writes them: a
 *     `<strong>`, `<em>` or `<u>` carrying `data-wend-mark`, and a `<span>`
 *     carrying `data-wend-mark="size"` with a `data-size` the contract allows.
 *     Every other element — a Word `<p class="MsoNormal">`, a `<div>`, a
 *     `<b>` the browser made on its own, a `style="font-weight:700"` — is
 *     **reduced to its text**: its children are walked, its own tag and
 *     attributes contribute nothing. `style` is never read, `class` is never
 *     read, and no attribute value ever becomes text or a mark except
 *     `data-size`, and only when it names one of the seven sizes.
 *   - {@link renderRuns} builds the surface from runs with `createElement` and
 *     `createTextNode`. There is no `innerHTML` in it and there must never be
 *     one: a run's text is a text node, so `<script>` typed into a paragraph is
 *     eleven characters, which is exactly what it is.
 *
 * ## What is dropped rather than read
 *
 * `<script>`, `<style>`, `<template>`, `<noscript>`, `<head>`, `<title>`,
 * `<meta>`, `<link>`, `<object>`, `<iframe>`, `<svg>` and `<xml>` contribute
 * nothing at all, not even their text. "Reduced to its text" means the text a
 * reader would have seen, and nobody sees the body of a `<style>` — a Word
 * paste carries kilobytes of it, and walking it would put `mso-bidi-font-size`
 * into somebody's newsletter. Comments are not elements and are skipped too.
 *
 * ## Why the walker exists at all when paste is intercepted
 *
 * The editor never lets a paste or a drop write HTML: `ParagraphEditor` takes
 * `text/plain` from the clipboard and inserts it through the run algebra, and
 * refuses drops. So in ordinary use the surface only ever holds what
 * `renderRuns` wrote plus characters typed into its text nodes. The walker is
 * still whitelist-first because "ordinary use" is not all use: browser
 * extensions write into editable regions, spell-check replacements and IME
 * commits go through the browser's own editing code, undo restores whatever the
 * browser recorded, and a `beforeinput` this editor did not anticipate is still
 * handled by the browser. Whatever any of those leaves behind is read as text,
 * and the next redraw replaces it with canonical markup.
 *
 * ## Line breaks
 *
 * The surface is `white-space: pre-wrap`, so a newline is a `\n` in a text
 * node, not an element. A `<br>` or a block element can still appear through
 * the routes above, and they are read the way they render: a `<br>` followed by
 * more content is a line break, a trailing `<br>` is the browser's placeholder
 * that gives an empty last line its height (so it counts as nothing), and a
 * block element starts a new line. {@link renderRuns} writes such a trailing
 * `<br>` itself after a paragraph ending in a newline, because without one the
 * browser draws no empty last line and the caret has nowhere to go.
 *
 * ## Why the walker takes a structural type
 *
 * {@link EditableNode} is the four members of `Node` the walker reads. A real
 * DOM node satisfies it; so does a tree a verify suite builds by hand, which is
 * how the hostile-paste probes run under `tsx` with no DOM library installed.
 */
import { textRunLineHeightPx } from '@wend/shared';

import {
  BODY_TEXT_SIZE,
  TEXT_RUN_FLAGS,
  isTextRunSize,
  normaliseRuns,
  runOf,
  type RunMarks,
  type TextRun,
  type TextRunFlag,
} from './text-runs';

/** The members of a DOM `Node` the walker reads. See the header. */
export interface EditableNode {
  readonly nodeType: number;
  readonly nodeName: string;
  readonly nodeValue: string | null;
  readonly childNodes: ArrayLike<EditableNode>;
  getAttribute?(name: string): string | null;
}

/** A DOM boundary point — a selection's anchor or focus. */
export interface EditablePoint {
  readonly node: EditableNode;
  readonly offset: number;
}

const ELEMENT_NODE = 1;
const TEXT_NODE = 3;

/** The attribute every mark element carries, and the only one read as a mark. */
export const MARK_ATTRIBUTE = 'data-wend-mark';
export const SIZE_ATTRIBUTE = 'data-size';

/** Which element each flag is drawn as. Read back only in this exact pairing. */
const FLAG_ELEMENT: Readonly<Record<TextRunFlag, string>> = {
  bold: 'STRONG',
  italic: 'EM',
  underline: 'U',
};

/** Elements whose content nobody sees. Dropped whole — see the header. */
const INVISIBLE = new Set([
  'SCRIPT',
  'STYLE',
  'TEMPLATE',
  'NOSCRIPT',
  'HEAD',
  'TITLE',
  'META',
  'LINK',
  'OBJECT',
  'IFRAME',
  'SVG',
  'XML',
]);

/** Elements that start a line of their own. */
const BLOCK = new Set([
  'ADDRESS',
  'ARTICLE',
  'ASIDE',
  'BLOCKQUOTE',
  'DD',
  'DIV',
  'DL',
  'DT',
  'FIGURE',
  'FOOTER',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
  'HEADER',
  'HR',
  'LI',
  'MAIN',
  'NAV',
  'OL',
  'P',
  'PRE',
  'SECTION',
  'TABLE',
  'TR',
  'UL',
]);

/**
 * A text node's characters as the contract wants them: CRLF and CR as LF, and
 * nothing else changed.
 *
 * A no-break space is **kept**. It was once mapped to a plain space, on the
 * theory that only a browser writes one; but every input reads the whole
 * paragraph back through here, so one keystroke anywhere rewrote every U+00A0
 * in it — "Rs.\u00a01,000" saved from the old textarea lost the no-break
 * space that keeps the amount on one line. The surface is `pre-wrap`, where a
 * browser has no reason to write one for a run of spaces, so what is read is
 * what was there.
 */
function cleanText(value: string): string {
  return value.replace(/\r\n?/g, '\n');
}

/** The flag or size this element is drawn for, when it is one this file wrote. */
function markOf(element: EditableNode): { flag: TextRunFlag } | { size: RunMarks['size'] } | null {
  const mark = element.getAttribute?.(MARK_ATTRIBUTE) ?? null;
  if (mark === null) return null;
  const name = element.nodeName.toUpperCase();
  for (const flag of TEXT_RUN_FLAGS) {
    if (mark === flag && name === FLAG_ELEMENT[flag]) return { flag };
  }
  if (mark === 'size' && name === 'SPAN') {
    const size = Number(element.getAttribute?.(SIZE_ATTRIBUTE) ?? '');
    if (Number.isInteger(size) && isTextRunSize(size)) return { size };
  }
  return null;
}

/**
 * The surface's runs, normalised, and where each of `points` falls in the
 * joined text.
 *
 * A point that the walk never reaches — one outside `root`, or inside an
 * element whose content is dropped — comes back at the position the walk had
 * reached when it passed that element, or at the end.
 */
export function readRuns(
  root: EditableNode,
  points: readonly EditablePoint[] = [],
): { runs: TextRun[]; offsets: (number | null)[] } {
  const runs: TextRun[] = [];
  const offsets: (number | null)[] = points.map(() => null);

  let length = 0;
  /** At the start of a line: nothing written yet, or the last thing was `\n`. */
  let atLineStart = true;
  /** `<br>`s seen and not yet committed — see "Line breaks" in the header. */
  let pendingBreaks = 0;
  /** A block ended or began: the next content starts a new line. */
  let needBreak = false;

  const write = (text: string, marks: RunMarks): void => {
    if (text === '') return;
    runs.push(runOf(text, marks));
    length += text.length;
    atLineStart = text.endsWith('\n');
  };

  /** Commits what is pending, in front of content that is about to be written. */
  const settle = (marks: RunMarks): void => {
    if (needBreak && !atLineStart) write('\n', marks);
    needBreak = false;
    while (pendingBreaks > 0) {
      write('\n', marks);
      pendingBreaks -= 1;
    }
  };

  /** A block boundary: the last pending `<br>` was a placeholder. */
  const boundary = (marks: RunMarks): void => {
    if (pendingBreaks > 0) {
      pendingBreaks -= 1;
      settle(marks);
    }
    needBreak = true;
  };

  const where = (): number => length + pendingBreaks + (needBreak && !atLineStart ? 1 : 0);

  const record = (node: EditableNode, offset: number, at: number): void => {
    points.forEach((point, index) => {
      if (offsets[index] === null && point.node === node && point.offset === offset) offsets[index] = at;
    });
  };

  const walk = (node: EditableNode, marks: RunMarks): void => {
    if (node.nodeType === TEXT_NODE) {
      const raw = node.nodeValue ?? '';
      const text = cleanText(raw);
      if (text !== '') settle(marks);
      const start = length;
      points.forEach((point, index) => {
        if (offsets[index] === null && point.node === node) {
          const within = cleanText(raw.slice(0, Math.max(0, point.offset))).length;
          offsets[index] = text === '' ? where() : start + within;
        }
      });
      write(text, marks);
      return;
    }

    if (node.nodeType !== ELEMENT_NODE) return;

    const name = node.nodeName.toUpperCase();
    if (INVISIBLE.has(name)) {
      points.forEach((point, index) => {
        if (offsets[index] === null && point.node === node) offsets[index] = where();
      });
      return;
    }

    if (name === 'BR') {
      pendingBreaks += 1;
      return;
    }

    const block = BLOCK.has(name);
    if (block && (length > 0 || pendingBreaks > 0)) boundary(marks);
    const lengthBefore = length;
    const breaksBefore = pendingBreaks;

    const mark = markOf(node);
    const inner: RunMarks =
      mark === null ? marks : 'flag' in mark ? { ...marks, [mark.flag]: true } : { ...marks, size: mark.size };

    const children = node.childNodes;
    for (let index = 0; index < children.length; index += 1) {
      record(node, index, where());
      const child = children[index];
      if (child !== undefined) walk(child, inner);
    }
    record(node, children.length, where());

    if (block) {
      // An empty block holding only its placeholder `<br>` is an empty line —
      // `<div><br></div>` is how a browser draws one — so it is written as a
      // line with nothing on it before the boundary that ends it.
      if (length === lengthBefore && pendingBreaks === breaksBefore + 1) {
        pendingBreaks -= 1;
        settle(marks);
        atLineStart = false;
      }
      boundary(marks);
    }
  };

  const plain: RunMarks = { bold: false, italic: false, underline: false, size: null };
  const children = root.childNodes;
  for (let index = 0; index < children.length; index += 1) {
    record(root, index, where());
    const child = children[index];
    if (child !== undefined) walk(child, plain);
  }
  record(root, children.length, where());

  // The end is a boundary: a trailing `<br>` was a placeholder.
  if (pendingBreaks > 0) {
    pendingBreaks -= 1;
    settle(plain);
  }

  return {
    runs: normaliseRuns(runs),
    offsets: offsets.map((offset) => (offset === null ? null : Math.min(offset, length))),
  };
}

/* -------------------------------------------------------------------------- */
/* Drawing                                                                     */
/* -------------------------------------------------------------------------- */

/** The members of `Document` the builder uses. */
export interface SurfaceDocument {
  createElement(name: string): HTMLElement;
  createTextNode(text: string): Text;
}

/** One run as nodes: the text, inside `<u>`, `<em>`, `<strong>`, then the size. */
function buildRun(doc: SurfaceDocument, run: TextRun): Node {
  let node: Node = doc.createTextNode(run.text);
  const wrap = (name: string, mark: string): HTMLElement => {
    const element = doc.createElement(name);
    element.setAttribute(MARK_ATTRIBUTE, mark);
    element.appendChild(node);
    return element;
  };
  if (run.underline === true) node = wrap('u', 'underline');
  if (run.italic === true) node = wrap('em', 'italic');
  if (run.bold === true) node = wrap('strong', 'bold');
  if (run.size !== undefined) {
    const span = wrap('span', 'size');
    span.setAttribute(SIZE_ATTRIBUTE, String(run.size));
    // Relative to the surface's own type, so 32 is twice the body size here as
    // it is in the email. A style the walker never reads: the size comes back
    // from `data-size` and nothing else.
    span.style.fontSize = `${String(run.size / BODY_TEXT_SIZE)}em`;
    // And at the email's own leading for that size, as a ratio of it.
    span.style.lineHeight = String(textRunLineHeightPx(run.size) / run.size);
    node = span;
  }
  return node;
}

/** The nodes `runs` are drawn as, trailing placeholder included. */
export function buildRunNodes(doc: SurfaceDocument, runs: readonly TextRun[]): Node[] {
  const nodes = runs.map((run) => buildRun(doc, run));
  const last = runs[runs.length - 1];
  if (last !== undefined && last.text.endsWith('\n')) nodes.push(doc.createElement('br'));
  return nodes;
}

/** Replaces the surface's content with `runs`. No HTML string is ever parsed. */
export function renderRuns(root: HTMLElement, runs: readonly TextRun[]): void {
  root.replaceChildren(...buildRunNodes(root.ownerDocument, runs));
}

/**
 * True when the surface holds exactly what {@link renderRuns} would draw for
 * `runs`. The editor redraws only when this is false, which is what keeps a
 * caret still and an IME composition intact through ordinary typing.
 */
export function isCanonical(root: HTMLElement, runs: readonly TextRun[]): boolean {
  const expected = buildRunNodes(root.ownerDocument, runs);
  const actual = root.childNodes;
  if (actual.length !== expected.length) return false;
  return expected.every((node, index) => {
    const other = actual[index];
    return other !== undefined && node.isEqualNode(other);
  });
}

/**
 * The DOM point at text offset `offset` in a canonical surface.
 *
 * At a boundary between two runs the caret goes at the end of the earlier one,
 * so typing after a bold word goes on in bold — the same choice `insertText`
 * makes, so what the caret shows and what typing does agree.
 */
export function pointAt(root: HTMLElement, offset: number): { node: Node; offset: number } {
  const doc = root.ownerDocument;
  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let position = 0;
  let last: Text | null = null;
  for (let node = walker.nextNode(); node !== null; node = walker.nextNode()) {
    const text = node as Text;
    const length = text.data.length;
    if (offset <= position + length) return { node: text, offset: offset - position };
    position += length;
    last = text;
  }
  return last === null ? { node: root, offset: 0 } : { node: last, offset: last.data.length };
}

/** Puts the selection on [start, end) of a canonical surface. */
export function selectRange(root: HTMLElement, start: number, end: number): void {
  const selection = root.ownerDocument.getSelection();
  if (selection === null) return;
  const from = pointAt(root, start);
  const to = pointAt(root, end);
  const range = root.ownerDocument.createRange();
  range.setStart(from.node, from.offset);
  range.setEnd(to.node, to.offset);
  selection.removeAllRanges();
  selection.addRange(range);
}

/** The selection as text offsets into the surface, or null when it is elsewhere. */
export function selectionOffsets(root: HTMLElement): { start: number; end: number } | null {
  const selection = root.ownerDocument.getSelection();
  if (selection === null || selection.rangeCount === 0) return null;
  const { anchorNode, focusNode } = selection;
  if (anchorNode === null || focusNode === null) return null;
  if (!root.contains(anchorNode) || !root.contains(focusNode)) return null;
  const { offsets } = readRuns(root, [
    { node: anchorNode, offset: selection.anchorOffset },
    { node: focusNode, offset: selection.focusOffset },
  ]);
  const [anchor, focus] = offsets;
  if (anchor === null || anchor === undefined || focus === null || focus === undefined) return null;
  return { start: Math.min(anchor, focus), end: Math.max(anchor, focus) };
}
